import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Page } from "@app/components/page";
import { default as Filter } from "@app/components/filter";
import { default as EmptyList } from "@app/components/list/empty";
import { default as confirm } from "@app/components/confirm";
import { Card, CitationModal, ReferenceMenu } from "@app/components/citation";
import { useLocation, useHistory } from "react-router-dom";
import qs from "qs";
import { COMMENT_CATEGORY } from "@app/constants";
import Sidebar from "./sidebar";
import notify from "@app/components/notify";
import { MultiSelect, MultiAction } from "@app/components/multi-select";
import { MultiSelectCheckBox } from "@app/components/multi-select/multi-select";
import { CitationType } from "@app/state/model/citation";
import { Context as MultiSelectContext } from "@app/components/multi-select/context";

import state from "@app/state/store/report/citation/list";
import report from "@app/state/store/report";
import projectDetailState from "@app/state/store/projects/details";

import "./styles/citation.scoped.scss";

const CitationsList = observer((props) => {
    const { project } = useParams();
    const [citation, setCitation] = useState(null);
    const config = report?.config ?? {};
    const location = useLocation();
    const history = useHistory();
    const readonly = report.readOnly;
    const { id } = qs.parse(location.search, {
        ignoreQueryPrefix: true,
    });
    const category = COMMENT_CATEGORY.REFERENCE;

    useEffect(() => {
        state.load();
        projectDetailState.id = project;
        projectDetailState.loadComments("desc", category);
    }, []);

    useEffect(() => {
        if (id && state.list) {
            const foundCitation = state.list.find((citation) => citation._id === id);

            if (foundCitation) {
                setCitation(foundCitation);
            }
        }
    }, [state.list, id]);

    const onEdit = (citation) => {
        setCitation(citation);
    };

    const onRemove = async (citation) => {
        let proceed = await confirm("Are you sure you want to remove this reference?");
        if (proceed) {
            await state.remove(citation._id);
        }
    };

    const onUpload = async (citation, file) => {
        citation.file = file._id;
        state.save({
            _id: citation._id,
            file: citation.file,
        });
    };

    const closeModal = () => {
        setCitation(null);

        if (id) {
            history.push(`${location.pathname}`);
        }
    };

    const onSubmit = async (values) => {
        state.save(citation._id ? { ...citation, ...values } : values);
        closeModal();
    };

    const onCancel = () => closeModal();

    const onFetch = (data) => {
        return state.fetchCitation(data);
    };

    const handleManualAdd = () => {
        setCitation({});
    };

    const customCitationIds = (state.list || [])
        .filter((c) => c.type === CitationType.CUSTOM)
        .map((c) => c._id);

    // Bulk delete handler
    const handleBulkDelete = async (selected) => {
        const selectedIds = selected.filter((item) => item.checked).map((item) => item._id);
        if (!selectedIds.length) {
            notify.info("Please select at least one reference to delete.");
            return;
        }
        let proceed = await confirm(
            `Are you sure you want to delete ${selectedIds.length} custom reference(s)?`,
        );
        if (proceed) {
            for (const id of selectedIds) {
                await state.remove(id);
            }
        }
    };

    const handleBulkDeleteAll = async () => {
        let proceed = await confirm(
            "Are you sure you want to delete ALL custom references in this project?",
        );
        if (!proceed) {
            return;
        }

        await state.removeAll();
    };

    return (
        <MultiSelect totalList={customCitationIds}>
            <MultiSelectContext.Consumer>
                {() => {
                    return (
                        <Page className="citations">
                            <Page.Header>
                                <Page.Header.Left shrink>
                                    <Page.Title>References</Page.Title>
                                </Page.Header.Left>
                                <Page.Header.Right>
                                    {!readonly && (
                                        <>
                                            <ReferenceMenu onManualAdd={handleManualAdd} />
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => setCitation({})}
                                            >
                                                Add Reference
                                            </Button>
                                        </>
                                    )}
                                </Page.Header.Right>
                            </Page.Header>
                            <Filter.Header
                                filter={state.filter}
                                showMultiAction
                                multiAction={
                                    <MultiAction
                                        handleDelete={handleBulkDelete}
                                        handleDeleteAll={handleBulkDeleteAll}
                                    />
                                }
                            />
                            <Page.Layout>
                                <Page.Body filter={state.filter}>
                                    <EmptyList
                                        title="No Results Found"
                                        show={state.list.length === 0 && !state.loading}
                                    >
                                        You can use the <b>Add</b> button to add more
                                    </EmptyList>
                                    {state.list.map((citation) => {
                                        const isCustom = citation.type === CitationType.CUSTOM;
                                        const isDue = citation.type === "due";
                                        const isSota = citation.type === "sota";
                                        const dueDisabled = isDue && !config.slr?.enabled;
                                        const sotaDisabled =
                                            isSota && !config.sota?.workflowEnabled;
                                        const disabled = dueDisabled || sotaDisabled;
                                        const handleClick = (e) => {
                                            if (disabled) {
                                                e.preventDefault();
                                                notify.info(
                                                    dueDisabled
                                                        ? "The DUE workflow is currently disabled. To reference articles from this workflow, please go to Settings to enable it."
                                                        : "The SoTA workflow is currently disabled. To reference articles from this workflow, please go to Settings to enable it.",
                                                );
                                            }
                                        };
                                        return (
                                            <div
                                                key={citation._id}
                                                onClick={handleClick}
                                                tabIndex={disabled ? -1 : 0}
                                                aria-disabled={disabled}
                                                style={disabled ? { pointerEvents: "auto" } : {}}
                                            >
                                                <Card
                                                    citation={citation}
                                                    refStyle={config.reference?.style}
                                                    onEdit={onEdit}
                                                    onRemove={onRemove}
                                                    onUpload={(file) => onUpload(citation, file)}
                                                    readonly={readonly}
                                                    disabled={disabled}
                                                    multiSelectCheckbox={
                                                        isCustom && !readonly ? (
                                                            <MultiSelectCheckBox
                                                                id={citation._id}
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </Page.Body>
                                <Sidebar
                                    state={projectDetailState}
                                    filter={state.filter}
                                    category={category}
                                />
                            </Page.Layout>

                            <Page.Footer>
                                <Filter.Pager filter={state.filter} />
                            </Page.Footer>

                            <CitationModal
                                open={!!citation}
                                citation={citation?._id ? citation : undefined}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                onFetch={onFetch}
                            />
                        </Page>
                    );
                }}
            </MultiSelectContext.Consumer>
        </MultiSelect>
    );
});

export default CitationsList;
